/**
 * Orders validation contract.
 *
 * The API and the browser share these schemas. Every failure message is a
 * message key, so a rejected line stored today renders in the reader's locale.
 */
import {
   currencyField,
   dateField,
   defineCalendar,
   defineImporter,
   fieldText,
   IMPORT_LIMITS,
   listQuerySchema,
   priceField,
   quantityField,
   required,
   textField
} from '@daka/core';
import { z } from 'zod';

import { ORDER_ACTION } from './constants';
import { ORDER_STATUSES } from './lifecycle';
import { ORDER_MESSAGES } from './messages';

// ============================================================================
// Order lines — the contract with the ERP
// ============================================================================

/**
 * One order line, as the ERP needs it. Every field is a core field type; only
 * what is specific to this contract (a SKU's length, quantities in whole units)
 * is set here. Complete and line PATCH use the same schema as the import editor.
 */
/** Line limits: the contract, the editor and the `order_lines` columns all use these. */
export const ORDER_LIMITS = {
   MAX_SKU_LENGTH: 120,
   MAX_DESCRIPTION_LENGTH: 1000,
   MAX_CUSTOMER_REF_LENGTH: 120
} as const;

export const orderRowSchema = z.object({
   sku: required(textField({ max: ORDER_LIMITS.MAX_SKU_LENGTH })),
   description: textField({ max: ORDER_LIMITS.MAX_DESCRIPTION_LENGTH }),
   quantity: required(quantityField()),
   unitPrice: priceField(),
   currency: currencyField(),
   customerRef: textField({ max: ORDER_LIMITS.MAX_CUSTOMER_REF_LENGTH }),
   deliveryDate: dateField()
});
export type OrderLineInput = z.infer<typeof orderRowSchema>;
/** A line as the editor sends it: each field as text, parsed once by the API. */
export type OrderLineText = z.input<typeof orderRowSchema>;

/**
 * A stored line back as text (`unitPrice` 450 → `"4.50"`), so it can go through
 * `orderRowSchema` again — stored values are never re-parsed as if they were typed.
 */
export function orderLineText(line: OrderLineInput): Record<keyof OrderLineInput, string> {
   const shape = orderRowSchema.shape;
   return {
      sku: fieldText(shape.sku, line.sku),
      description: fieldText(shape.description, line.description),
      quantity: fieldText(shape.quantity, line.quantity),
      unitPrice: fieldText(shape.unitPrice, line.unitPrice),
      currency: fieldText(shape.currency, line.currency),
      customerRef: fieldText(shape.customerRef, line.customerRef),
      deliveryDate: fieldText(shape.deliveryDate, line.deliveryDate)
   };
}

/**
 * Order lines from a spreadsheet. `aliases` are the header spellings that map onto
 * each field, compared as lowercase alphanumerics ("Unit Price" = "unit_price").
 */
export const ORDER_LINE_IMPORTER = defineImporter({
   id: 'lines',
   action: ORDER_ACTION.CREATE,
   fields: {
      sku: { labelKey: ORDER_MESSAGES.columnSku, aliases: ['sku', 'itemcode', 'productcode', 'code', 'article'], unique: true },
      description: { labelKey: ORDER_MESSAGES.columnDescription, aliases: ['description', 'desc', 'productname', 'name', 'item'] },
      quantity: { labelKey: ORDER_MESSAGES.columnQuantity, aliases: ['quantity', 'qty', 'units'] },
      unitPrice: { labelKey: ORDER_MESSAGES.columnUnitPrice, aliases: ['unitprice', 'price', 'rate', 'cost'] },
      currency: { labelKey: ORDER_MESSAGES.columnCurrency, aliases: ['currency', 'ccy'] },
      customerRef: {
         labelKey: ORDER_MESSAGES.columnCustomerRef,
         aliases: ['customerref', 'customerreference', 'reference', 'ref', 'ponumber', 'po']
      },
      deliveryDate: { labelKey: ORDER_MESSAGES.columnDeliveryDate, aliases: ['deliverydate', 'date', 'duedate', 'requireddate'] }
   },
   rowSchema: orderRowSchema
});

/** Placeholder ERP payload: one order header + its lines (1 file = 1 order), lines given as text (`orderLineText`). */
export const orderPayloadSchema = z.object({
   orderId: z.uuid(),
   organizationId: z.uuid(),
   filename: z.string().min(1).max(IMPORT_LIMITS.MAX_FILENAME_LENGTH),
   lines: z.array(orderRowSchema).min(1)
});
export type OrderPayload = z.infer<typeof orderPayloadSchema>;

// ============================================================================
// Jobs
// ============================================================================

/** BullMQ queues, declared once: the API enqueues and the worker consumes exactly this shape. */
export const ORDER_QUEUE = {
   /** Just the id — the worker re-reads the order row. */
   SUBMIT: { name: 'orders.submit', schema: z.object({ orderId: z.uuid() }) }
} as const;
export type SubmitJobData = z.infer<typeof ORDER_QUEUE.SUBMIT.schema>;

// ============================================================================
// Requests
// ============================================================================

export const ORDER_SORTS = ['filename', 'status', 'lineCount', 'updatedAt', 'createdAt'] as const;
export const listOrdersQuerySchema = listQuerySchema(ORDER_SORTS).extend({
   status: z.enum(ORDER_STATUSES).optional()
});
export type ListOrdersQuery = z.infer<typeof listOrdersQuerySchema>;

export const ORDER_LINE_SORTS = ['rowNumber', 'sku', 'quantity', 'unitPrice'] as const;
export const listOrderLinesQuerySchema = listQuerySchema(ORDER_LINE_SORTS);
export type ListOrderLinesQuery = z.infer<typeof listOrderLinesQuerySchema>;

// ============================================================================
// Purchasing appointments (FO §6.1, F-001…F-003)
// ============================================================================

const requiredText = required(textField({ max: 200 }));
const optionalText = textField({ max: 200 });

/** Purchasing appointments on the core calendar engine. */
export const ORDER_APPOINTMENTS = defineCalendar({
   id: 'appointments',
   readAction: ORDER_ACTION.READ,
   writeAction: ORDER_ACTION.CREATE,
   fields: {
      supplier: { labelKey: ORDER_MESSAGES.fieldSupplier },
      contactPerson: { labelKey: ORDER_MESSAGES.fieldContactPerson },
      season: { labelKey: ORDER_MESSAGES.fieldSeason },
      location: { labelKey: ORDER_MESSAGES.fieldLocation },
      buyer: { labelKey: ORDER_MESSAGES.fieldBuyer },
      brand: { labelKey: ORDER_MESSAGES.fieldBrand },
      subSeason: { labelKey: ORDER_MESSAGES.fieldSubSeason },
      assortment: { labelKey: ORDER_MESSAGES.fieldAssortment }
   },
   dataSchema: z.object({
      supplier: requiredText,
      contactPerson: requiredText,
      season: requiredText,
      location: requiredText,
      buyer: optionalText,
      brand: optionalText,
      subSeason: optionalText,
      assortment: optionalText
   }),
   filters: ['buyer', 'supplier', 'brand', 'season', 'subSeason', 'assortment'],
   titleField: 'supplier'
});
